import {
	Activity,
	AlertTriangle,
	CreditCard,
	Gauge,
	LayoutDashboard,
	ScrollText,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from '@remix-run/react'
import { CheckCircleIcon } from '@heroicons/react/24/solid'

import { Card, CardContent, CardHeader } from '#app/components/ui/card.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { cn } from '#app/utils/misc.tsx'
import { Separator } from '#app/components/ui/separator.tsx'
import { Tabs, TabsList, TabsTrigger } from '#app/components/ui/tabs.tsx'

const INITIAL = 'Developer Portal'
const TABS = [
	{
		title: INITIAL,
		icon: <LayoutDashboard className="h-4 w-4" />,
		header: 'Beautiful developer experience',
		description: `Allow developers to manage their team members, API keys, monitor their API usage, buy API credits, and view their invoices all inside a single beautiful dashboard.`,
		image: (
			<Card className="w-full max-w-md rounded-xl bg-white text-black">
				<CardHeader></CardHeader>
			</Card>
		),
	},
	{
		title: 'Price Plans',
		icon: <CreditCard className="h-4 w-4" />,
		header: 'Flexible price plans',
		description: `Set up multiple price plans for your customers with flexible rate limits, usage metering, and prices for each plan.`,
		image: <Plans />,
	},
	{
		title: 'Usage Metering',
		icon: <Activity className="h-4 w-4" />,
		header: 'Real-time usage metering',
		description:
			`Allow developers to monitor their API usage.\n\n` +
			`Developers receive notifications to upgrade plans or buy additional API credits when their account balance is running low.`,
		image: (
			<Feature className="from-pink-400 to-fuchsia-400">
				<div className="flex w-5 items-center justify-center">
					<AlertTriangle className="text-orange-600" />
				</div>
				<div className="text-sm text-slate-500">
					<strong className="text-slate-700">You</strong> have used up{' '}
					<strong className="text-red-700">90%</strong> of your monthly API
					credits.
				</div>
			</Feature>
		),
	},
	{
		title: 'Rate Limiting',
		icon: <Gauge className="h-4 w-4" />,
		header: 'Real-time rate limiting',
		description:
			`Protect your API servers from catastrophic DOS (denial of service) attacks. ` +
			`Apply flexible rate limits to your customers at an account, API key, or API level.\n\n` +
			`Notify developers when they exceed their account rate limits so they can manage ramifications in their application.`,
		image: (
			<Feature className="from-indigo-400 to-blue-400">
				<div className="flex w-5 items-center justify-center">
					<AlertTriangle className="text-orange-600" />
				</div>
				<div className="grid gap-1 text-sm text-slate-500">
					<div>
						<strong className="text-slate-700">429 — Too Many Requests</strong>
					</div>
					<div>Exceeded rate limit of 60 requests per minute.</div>
				</div>
			</Feature>
		),
	},
	{
		title: 'Invoicing',
		icon: <ScrollText className="h-4 w-4" />,
		header: 'Fully-managed invoicing',
		description: `Subdark collects payments, sends invoices, and manages taxes for your customers. Just wait to receive your profits at the end of each month.`,
		image: <img src="/img/invoice.svg" alt="invoice" />,
	},
]

export function Features() {
	const [selected, setSelected] = useState(INITIAL)
	const sTab = TABS.find((t) => t.title === selected)
	if (!sTab) return null
	return (
		<div className="container pt-24">
			<Card className="w-full bg-white/80 dark:bg-neutral-950/80">
				<CardHeader className="grid items-center gap-x-8 gap-y-0 space-y-0 divide-y sm:grid-cols-5 sm:divide-y-0">
					{TABS.map((tab) => (
						<Button
							key={tab.title}
							variant="ghost"
							className={cn(
								'flex w-full gap-3 rounded-lg py-6 focus-within:ring-0 hover:bg-neutral-200/80 dark:hover:bg-neutral-800/80',
								tab.title === selected && 'bg-neutral-200 dark:bg-neutral-800',
							)}
							onClick={() => setSelected(tab.title)}
						>
							{tab.icon}
							{tab.title}
						</Button>
					))}
				</CardHeader>
				<Separator />
				<CardContent className="grid gap-8 pt-6 sm:grid-cols-2">
					<div className="col-span-1 flex flex-col gap-y-4 pr-8">
						<h3 className="text-h4 font-medium">{sTab.header}</h3>
						<h4 className="whitespace-pre-wrap text-neutral-600 dark:text-neutral-400">
							{sTab.description}
						</h4>
						<Link
							to="https://cal.com/stanliu/subdark-demo"
							target="_blank"
							className="mt-2"
						>
							<Button>Book a demo</Button>
						</Link>
					</div>
					<div className="col-span-1 flex items-center justify-center rounded-xl bg-neutral-200 p-8 dark:bg-neutral-800">
						{sTab.image}
					</div>
				</CardContent>
			</Card>
		</div>
	)
}

interface IFeature {
	className?: string
	children: React.ReactNode | React.ReactNode[]
}

function Feature(props: IFeature) {
	return (
		<div
			className={cn(
				'z-10 mx-auto flex w-full max-w-md items-center justify-center overflow-hidden rounded-lg bg-gradient-to-b from-teal-400 to-cyan-400 p-6 shadow transition-all duration-200 ease-out',
				props.className,
			)}
		>
			<div className="inline-flex items-center space-x-4 rounded-lg bg-white px-6 py-4 ring ring-slate-400/10">
				{props.children}
			</div>
		</div>
	)
}

type Interval = 'monthly' | 'annual'
type PlanType = 'Basic' | 'Pro' | 'Enterprise'
const PLANS = [
	{
		title: 'Basic' as const,
		requests: '1,000',
		price: {
			monthly: 'FREE',
			annual: 'FREE',
		},
		features: [
			'Unlimited templates',
			'Unlimited file uploads',
			'GPT-4 writing assistant',
			'5 free session replays',
		],
	},
	{
		title: 'Pro' as const,
		requests: '50,000',
		price: { monthly: '$500 / mo', annual: '$400 / mo' },
		features: [
			'Everything in Basic',
			'Unlimited recordings',
			'Slack integration',
			'Priority support',
		],
	},
	{
		title: 'Enterprise' as const,
		requests: '',
		price: { monthly: '$1,500 / mo', annual: '$1,200 / mo' },
		features: [
			'Everything in Basic',
			'Unlimited recordings',
			'Slack integration',
			'Priority support',
		],
	},
]

function Plans() {
	const [interval] = useState<Interval>('annual')
	const [selected, setSelected] = useState<PlanType>('Basic')
	return (
		<div className="flex w-full flex-col gap-4">
			{PLANS.map((plan) => (
				<PlanCard
					key={plan.title}
					selected={plan.title === selected}
					interval={interval}
					onSelect={() => setSelected(plan.title)}
					{...plan}
				/>
			))}
		</div>
	)
}

interface IPlanCard {
	selected: boolean
	title: string
	requests: string
	interval: Interval
	price: {
		monthly: string
		annual: string
	}
	onSelect: () => void
}

function PlanCard(props: IPlanCard) {
	const { selected, price, interval } = props
	return (
		<Card
			className={cn(
				'mx-auto w-full max-w-sm cursor-pointer border-2 border-neutral-200 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-950',
				selected && 'border-neutral-800 dark:border-neutral-200',
			)}
			onClick={props.onSelect}
		>
			<CardHeader className="p-4">
				<div className="flex items-center justify-between">
					<Subtitle className="min-h-[1.5rem]">{props.title}</Subtitle>
					{selected ? (
						<CheckCircleIcon className="h-5 w-5" />
					) : (
						<div className="h-6 w-6" />
					)}
				</div>
				<div className="flex items-center justify-between">
					<div className="flex items-end gap-1.5">
						<div className="text-xl font-semibold">
							{props.requests || 'Unlimited'}
						</div>
						{props.requests && <div className="text-sm">requests</div>}
					</div>
					<div className="text-base">{price[interval]}</div>
				</div>
			</CardHeader>
		</Card>
	)
}

function Subtitle({
	className,
	children,
}: {
	className?: string
	children: React.ReactNode
}) {
	return (
		<div
			className={cn(
				'text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400',
				className,
			)}
		>
			{children}
		</div>
	)
}
